import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { CheckCircle, XCircle, Clock, TrendingUp, AlertCircle, AlertTriangle, ArrowRight, Activity, Smartphone, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import clsx from 'clsx';
import { apiService, getApiBaseUrl } from '../services/api';

const TIME_FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7', label: 'Last 7 days' },
  { value: '15', label: 'Last 15 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '360', label: 'Last 360 days' },
];

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All sections' },
  { value: 'New Gear', label: 'New Gear only' },
  { value: 'Used Gear', label: 'Used Gear only' },
  { value: 'Connect', label: 'Connect only' },
];

function rangeFor(filter) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (filter === 'today') {
    return { start_date: startOfToday.toISOString(), end_date: now.toISOString() };
  }
  if (filter === 'yesterday') {
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    return { start_date: startOfYesterday.toISOString(), end_date: startOfToday.toISOString() };
  }
  const days = parseInt(filter, 10);
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { start_date: start.toISOString(), end_date: now.toISOString() };
}

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [mobileTrend, setMobileTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('30');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [selectedMobileRunId, setSelectedMobileRunId] = useState(null);
  const [mobileBreakdown, setMobileBreakdown] = useState(null);
  const [mobileBreakdownLoading, setMobileBreakdownLoading] = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(null);
  const [webChartPage, setWebChartPage] = useState(0);   // 0 = newest window
  const [mobileChartPage, setMobileChartPage] = useState(0);
  const CHART_PAGE_SIZE = 10;

  // Reset chart paging + run selection when the time window or category changes
  useEffect(() => {
    setWebChartPage(0);
    setMobileChartPage(0);
    setSelectedRunId(null);
    setSelectedMobileRunId(null);
  }, [timeFilter, categoryFilter]);

  // Auto-select the most recent run's breakdown when data first arrives
  // (or after a filter change cleared selection). User clicks override this.
  useEffect(() => {
    if (selectedRunId) return;
    const latest = data?.recent_runs?.[0];
    if (latest) setSelectedRunId(latest.id);
  }, [data, selectedRunId]);

  useEffect(() => {
    if (!selectedRunId) {
      setBreakdown(null);
      return;
    }
    setBreakdownLoading(true);
    apiService.getTestRunBreakdown(selectedRunId)
      .then((res) => setBreakdown(res.data))
      .catch(() => setBreakdown(null))
      .finally(() => setBreakdownLoading(false));
  }, [selectedRunId]);

  // Mobile breakdown: user-selected run wins, otherwise default to latest
  const latestMobileRunId = data?.mobile_recent_runs?.[0]?.id ?? null;
  const effectiveMobileRunId = selectedMobileRunId ?? latestMobileRunId;
  useEffect(() => {
    if (!effectiveMobileRunId) {
      setMobileBreakdown(null);
      return;
    }
    setMobileBreakdownLoading(true);
    apiService.getMobileTestRunBreakdown(effectiveMobileRunId)
      .then((res) => setMobileBreakdown(res.data))
      .catch(() => setMobileBreakdown(null))
      .finally(() => setMobileBreakdownLoading(false));
  }, [effectiveMobileRunId]);

  useEffect(() => {
    fetchData(timeFilter, categoryFilter);
    const id = setInterval(() => fetchData(timeFilter, categoryFilter), 30000);
    return () => clearInterval(id);
  }, [timeFilter, categoryFilter]);

  const fetchData = async (filter, cat) => {
    const range = rangeFor(filter);
    const dashParams = cat && cat !== 'all' ? { ...range, category: cat } : range;
    try {
      const [dashRes, trendRes] = await Promise.all([
        apiService.getDashboardDataV2(dashParams),
        // Mobile trend doesn't apply when a section filter is active
        cat && cat !== 'all'
          ? Promise.resolve({ data: [] })
          : apiService.getMobileTestTrend(range).catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setMobileTrend(trendRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  const { summary, recent_runs, health_status, mobile_recent_runs, mobile_summary } = data;

  // Web test chart data (oldest → newest)
  const allWebChartData = recent_runs.slice().reverse().map((run) => {
    const passed = run.passed_tests || 0;
    const failed = run.failed_tests || 0;
    const skipped = run.skipped_tests || 0;
    const cloudflareBlocked = run.cloudflare_blocked_tests || 0;
    const total = run.total_tests || 0;
    // For running runs, show how many tests are still pending as a 4th segment
    const isRunning = run.status === 'running';
    const pending = isRunning ? Math.max(0, total - passed - failed - skipped) : 0;
    return {
      runId: run.id,
      name: format(new Date(run.timestamp), 'dd MMM'),
      time: format(new Date(run.timestamp), 'HH:mm'),
      status: run.status,
      passed,
      failed,
      skipped,
      cloudflareBlocked,
      pending,
      total,
    };
  });

  // Mobile test chart data (oldest → newest, daily aggregates).
  // Match by UTC date — t.date is a YYYY-MM-DD string from postgres func.date(),
  // run.timestamp is an ISO string like "2026-05-04T22:00:00Z". Comparing both
  // in UTC avoids the IST/UTC timezone shift that was making lookups fail.
  const allMobileChartData = mobileTrend.map((t) => {
    const runOnDay = (data?.mobile_recent_runs || []).find(
      (r) => (r.timestamp || '').startsWith(t.date)
    );
    return {
      name: format(new Date(t.date + 'T00:00:00'), 'dd MMM'),
      passed: t.total_passed,
      failed: t.total_failures,
      runId: runOnDay?.id ?? null,
    };
  });

  // Paginated slices — page 0 = newest window, higher pages go older
  const sliceForPage = (arr, page) => {
    const len = arr.length;
    const end = len - page * CHART_PAGE_SIZE;
    const start = Math.max(0, end - CHART_PAGE_SIZE);
    return { data: arr.slice(start, end), start, end, total: len };
  };
  const web = sliceForPage(allWebChartData, webChartPage);
  const mob = sliceForPage(allMobileChartData, mobileChartPage);
  const chartData = web.data;
  const mobileChartData = mob.data;

  // Most recent web run and most recent mobile run — shown side by side so
  // neither one hides the other when one suite ran more recently.
  const lastWebRun = summary.last_run;
  const lastMobileRun = mobile_recent_runs?.[0] || null;

  const webLast = lastWebRun
    ? {
        status: lastWebRun.status,
        time: format(new Date(lastWebRun.timestamp), 'MMM dd, HH:mm'),
      }
    : null;
  const mobileLast = lastMobileRun
    ? {
        status: lastMobileRun.total_failures > 0 ? 'partial' : 'passed',
        time: format(new Date(lastMobileRun.timestamp), 'MMM dd, HH:mm'),
      }
    : null;

  const filterLabel = TIME_FILTERS.find((f) => f.value === timeFilter)?.label || '';
  const subText = categoryFilter !== 'all'
    ? `${categoryFilter} · ${filterLabel.toLowerCase()}`
    : filterLabel.toLowerCase();
  const successPct = summary.success_rate || 0;

  // Build the plain-English status banner from latest run + health checks
  const banner = buildStatusBanner({
    latestRun: recent_runs?.[0],
    healthChecks: health_status || [],
    breakdown,
  });

  return (
    <div className="space-y-6">
      {/* Plain-English status headline — what's going on right now */}
      <StatusBanner banner={banner} />

      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Automated health monitoring for bajaao.com
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            {CATEGORY_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            {TIME_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      {(() => {
        // Tasteful one-extra-fact subtitles — answer the next question a non-technical viewer asks.
        // Success Rate and Total Runs are already combined web + mobile (see dashboard-v2).
        const totalCases = summary.total_test_cases || 0;
        const passedCases = summary.total_passed_cases || 0;
        const successSub = totalCases > 0
          ? `${passedCases.toLocaleString()} of ${totalCases.toLocaleString()} tests · web + mobile`
          : null;
        // Runs aren't uniform (web ≈ 517 tests ×4/day, mobile ≈ 37 tests ×1/day),
        // so "~N tests per run" would mislead. Show the web/mobile run split instead.
        const mobileRunsCount = mobile_summary?.total_runs || 0;
        const webRunsCount = (summary.total_runs || 0) - mobileRunsCount;
        const totalRunsSub = `${webRunsCount} web · ${mobileRunsCount} mobile`;

        // Avg Duration is split into web vs mobile: blending them hides a ~2x gap
        // and mixes very different run shapes. Web average is computed from the web
        // runs in the window; mobile average comes from mobile_summary. Shown in
        // minutes (rounded) for an easy read.
        const webDurationsMs = (recent_runs || []).map((r) => r.duration).filter((d) => d != null);
        const webAvgMin = webDurationsMs.length
          ? webDurationsMs.reduce((a, b) => a + b, 0) / webDurationsMs.length / 1000 / 60
          : null;
        const webPerRun = summary.tests_per_run || 0;
        const mobileAvgMin = mobile_summary?.avg_duration_seconds
          ? mobile_summary.avg_duration_seconds / 60
          : null;
        const mobilePerRun = mobile_recent_runs?.[0]?.total_tests || 0;
        const fmtMin = (m) => (m == null ? 'N/A' : `${Math.round(m)} min`);

        return (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard
              label="Success Rate"
              value={`${successPct.toFixed(1)}%`}
              sub={successSub}
              subDim={subText}
              color={successPct >= 90 ? 'green' : successPct >= 70 ? 'yellow' : 'red'}
            />
            <KpiCard
              label="Total Runs"
              value={summary.total_runs}
              sub={totalRunsSub}
              subDim={subText}
              color="blue"
            />
            <KpiCard
              label="Avg Web Run"
              value={fmtMin(webAvgMin)}
              sub={webPerRun > 0 ? `to run ${webPerRun} web tests` : 'web tests'}
              subDim={subText}
              color="gray"
            />
            <KpiCard
              label="Avg Mobile Run"
              value={fmtMin(mobileAvgMin)}
              sub={mobilePerRun > 0 ? `to run ${mobilePerRun} mobile tests` : 'no mobile runs'}
              subDim={subText}
              color="gray"
            />
            <LastRunCard web={webLast} mobile={mobileLast} />
          </div>
        );
      })()}

      {/* Web Test Chart + Recent Runs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-gray-700">Web Test Results Trend</h2>
            <div className="flex items-center gap-3">
              {selectedRunId && (
                <button
                  onClick={() => setSelectedRunId(null)}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  Clear selection
                </button>
              )}
              <span className="text-xs text-gray-400">
                {web.total > 0 ? `${web.start + 1}–${web.end} of ${web.total}` : 'No runs'}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setWebChartPage((p) => p + 1)}
                  disabled={web.start <= 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Older runs"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-500" />
                </button>
                <button
                  onClick={() => setWebChartPage((p) => Math.max(0, p - 1))}
                  disabled={webChartPage === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Newer runs"
                >
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                barCategoryGap="20%"
                onClick={(e) => {
                  const payload = e?.activePayload?.[0]?.payload;
                  if (payload?.runId) setSelectedRunId(payload.runId);
                }}
              >
                <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={30} />
                <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<RunBarTooltip />} />
                <Bar dataKey="passed" stackId="a" radius={[0, 0, 0, 0]} cursor="pointer">
                  {chartData.map((d, i) => (
                    <Cell key={i} fill="#22c55e" fillOpacity={selectedRunId && d.runId !== selectedRunId ? 0.35 : 1} />
                  ))}
                </Bar>
                <Bar dataKey="failed" stackId="a" radius={[0, 0, 0, 0]} cursor="pointer">
                  {chartData.map((d, i) => (
                    <Cell key={i} fill="#ef4444" fillOpacity={selectedRunId && d.runId !== selectedRunId ? 0.35 : 1} />
                  ))}
                </Bar>
                <Bar dataKey="skipped" stackId="a" radius={[0, 0, 0, 0]} cursor="pointer">
                  {chartData.map((d, i) => (
                    <Cell key={i} fill="#d1d5db" fillOpacity={selectedRunId && d.runId !== selectedRunId ? 0.35 : 1} />
                  ))}
                </Bar>
                {/* "Pending" segment for runs still in progress — striped blue */}
                <Bar dataKey="pending" stackId="a" radius={[4, 4, 0, 0]} cursor="pointer">
                  {chartData.map((d, i) => (
                    <Cell key={i} fill="#93c5fd" fillOpacity={selectedRunId && d.runId !== selectedRunId ? 0.25 : 0.7} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown panel */}
          {selectedRunId && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Run #{selectedRunId} — category breakdown
                </h3>
                <Link to={`/test-runs/${selectedRunId}`} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Full results <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              {breakdownLoading ? (
                <div className="h-40 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent" />
                </div>
              ) : breakdown ? (
                <>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { name: 'New Gear', passed: breakdown.new_gear.passed, failed: breakdown.new_gear.failed },
                          { name: 'Used Gear', passed: breakdown.used_gear.passed, failed: breakdown.used_gear.failed },
                          { name: 'Connect', passed: breakdown.connect.passed, failed: breakdown.connect.failed },
                        ]}
                        barCategoryGap="30%"
                      >
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={30} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        <Bar dataKey="passed" fill="#22c55e" name="Passed" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="failed" fill="#ef4444" name="Failed" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Failed test names per category — each card clickable to drill into the run */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { key: 'new_gear', label: 'New Gear' },
                      { key: 'used_gear', label: 'Used Gear' },
                      { key: 'connect', label: 'Connect' },
                    ].map((cat) => {
                      const b = breakdown[cat.key];
                      const failedTests = b.failed_tests || [];
                      const hasFails = b.failed > 0;
                      const cfBlocked = b.cloudflare_blocked || 0;
                      return (
                        <Link
                          key={cat.key}
                          to={`/test-runs/${selectedRunId}?category=${encodeURIComponent(cat.label)}`}
                          className={clsx(
                            'block rounded-lg border p-3 transition-colors hover:shadow-sm',
                            hasFails
                              ? 'border-error-200 bg-error-50/30 hover:bg-error-50/60'
                              : 'border-gray-200 hover:bg-gray-50'
                          )}
                          title={`Click to open Run #${selectedRunId} with ${cat.label} expanded`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-gray-800">{cat.label}</p>
                            <p className="text-xs">
                              <span className="text-success-600">{b.passed}</span>
                              <span className="text-gray-400"> / </span>
                              <span className={hasFails ? 'text-error-600 font-medium' : 'text-gray-400'}>
                                {b.failed} failed
                              </span>
                            </p>
                          </div>
                          {cfBlocked > 0 && (
                            <p
                              className="text-[11px] text-amber-700 mb-2 -mt-1"
                              title="Cloudflare bot-protection blocked these — not real failures, the site was unreachable to the runner."
                            >
                              ⚠ {cfBlocked} blocked by Cloudflare
                            </p>
                          )}
                          {hasFails ? (
                            <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                              {failedTests.map((t) => (
                                <li key={t.id} className="leading-tight">
                                  <p
                                    className="text-xs text-error-700 font-medium"
                                    title={t.suite ? `${t.suite} › ${t.title}` : t.title}
                                  >
                                    • {t.error_friendly || t.title}
                                  </p>
                                  {t.error_friendly && (
                                    <p className="text-[11px] text-gray-400 ml-2.5 mt-0.5">
                                      {t.title}
                                    </p>
                                  )}
                                  {t.summary && (
                                    <p className="text-[11px] text-gray-500 ml-2.5 mt-0.5 leading-snug">
                                      {t.summary}
                                    </p>
                                  )}
                                  {t.screenshot_path && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setScreenshotModal(t.screenshot_path);
                                      }}
                                      className="inline-flex items-center gap-1 ml-2.5 mt-1 px-1.5 py-0.5 text-[10px] font-medium rounded border border-error-300 bg-white text-error-700 hover:bg-error-50"
                                    >
                                      <ImageIcon className="h-2.5 w-2.5" /> View screenshot
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-success-600 italic">All passing</p>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No breakdown available</p>
              )}
            </div>
          )}
        </div>

        {/* Recent Runs */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-medium text-gray-700">Recent Runs</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {recent_runs.slice(0, 6).map((run) => (
              <Link
                key={`web-${run.id}`}
                to={`/test-runs/${run.id}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <StatusDot status={run.status} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-800">Run #{run.id}</p>
                      {run.status === 'running' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" /> RUNNING
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      {format(new Date(run.timestamp), 'MMM dd, HH:mm')}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs font-medium text-gray-600">
                    {run.passed_tests}/{run.total_tests}
                  </span>
                  {run.failed_tests > 0 && (
                    <p className="text-xs text-error-500">{run.failed_tests} failed</p>
                  )}
                  {run.cloudflare_blocked_tests > 0 && (
                    <p
                      className="text-[11px] text-amber-700"
                      title="Cloudflare bot-protection blocked these tests — not real failures, the site was unreachable to the runner."
                    >
                      ⚠ {run.cloudflare_blocked_tests} blocked by Cloudflare
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-gray-100">
            <Link to="/test-runs" className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1">
              View all runs <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Test Chart + Recent Mobile Runs */}
      {(mobileChartData.length > 0 || (mobile_recent_runs && mobile_recent_runs.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Mobile Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-primary-500" />
                Mobile App Test Results Trend
              </h2>
              <div className="flex items-center gap-3">
                {selectedMobileRunId && (
                  <button
                    onClick={() => setSelectedMobileRunId(null)}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Clear selection
                  </button>
                )}
                {mobile_summary && (
                  <span className="text-xs text-gray-400">
                    {mobile_summary.pass_rate}% pass rate
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {mob.total > 0 ? `${mob.start + 1}–${mob.end} of ${mob.total}` : 'No data'}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setMobileChartPage((p) => p + 1)}
                    disabled={mob.start <= 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Older days"
                  >
                    <ChevronLeft className="h-4 w-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => setMobileChartPage((p) => Math.max(0, p - 1))}
                    disabled={mobileChartPage === 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Newer days"
                  >
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
            {mobileChartData.length > 0 ? (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={mobileChartData}
                    barCategoryGap="20%"
                    onClick={(e) => {
                      const payload = e?.activePayload?.[0]?.payload;
                      if (payload?.runId) setSelectedMobileRunId(payload.runId);
                    }}
                  >
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={30} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="passed" stackId="a" name="Passed" cursor="pointer">
                      {mobileChartData.map((d, i) => (
                        <Cell
                          key={i}
                          fill="#22c55e"
                          fillOpacity={effectiveMobileRunId && d.runId !== effectiveMobileRunId ? 0.35 : 1}
                        />
                      ))}
                    </Bar>
                    <Bar dataKey="failed" stackId="a" name="Failed" radius={[4, 4, 0, 0]} cursor="pointer">
                      {mobileChartData.map((d, i) => (
                        <Cell
                          key={i}
                          fill="#ef4444"
                          fillOpacity={effectiveMobileRunId && d.runId !== effectiveMobileRunId ? 0.35 : 1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-56 flex items-center justify-center text-sm text-gray-400">
                No mobile test trend data yet
              </div>
            )}

            {/* Mobile category breakdown — auto-shows latest mobile run, or whichever bar the user clicked */}
            {effectiveMobileRunId && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    Mobile Run #{effectiveMobileRunId} — category breakdown
                    {selectedMobileRunId && selectedMobileRunId !== latestMobileRunId && (
                      <button
                        onClick={() => setSelectedMobileRunId(null)}
                        className="ml-2 text-xs text-gray-400 hover:text-gray-600"
                      >
                        (clear selection)
                      </button>
                    )}
                  </h3>
                  <Link to={`/mobile-tests/${effectiveMobileRunId}`} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
                    Full results <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                {mobileBreakdownLoading ? (
                  <div className="h-40 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent" />
                  </div>
                ) : mobileBreakdown ? (
                  <>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            { name: 'New Gear', passed: mobileBreakdown.new_gear?.passed || 0, failed: mobileBreakdown.new_gear?.failed || 0 },
                            { name: 'Used Gear', passed: mobileBreakdown.used_gear?.passed || 0, failed: mobileBreakdown.used_gear?.failed || 0 },
                            { name: 'Connect', passed: mobileBreakdown.connect?.passed || 0, failed: mobileBreakdown.connect?.failed || 0 },
                            ...(mobileBreakdown.unmapped
                              ? [{ name: 'Unmapped', passed: mobileBreakdown.unmapped.passed || 0, failed: mobileBreakdown.unmapped.failed || 0 }]
                              : []),
                          ]}
                          barCategoryGap="30%"
                        >
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={30} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                          <Bar dataKey="passed" fill="#22c55e" name="Passed" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="failed" fill="#ef4444" name="Failed" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { key: 'new_gear', label: 'New Gear' },
                        { key: 'used_gear', label: 'Used Gear' },
                        { key: 'connect', label: 'Connect' },
                      ].map((cat) => {
                        const b = mobileBreakdown[cat.key] || { passed: 0, failed: 0, failed_tests: [] };
                        const failedTests = b.failed_tests || [];
                        const hasFails = b.failed > 0;
                        return (
                          <Link
                            key={cat.key}
                            to={`/mobile-tests/${effectiveMobileRunId}?category=${encodeURIComponent(cat.label)}`}
                            className={clsx(
                              'block rounded-lg border p-3 transition-colors hover:shadow-sm',
                              hasFails
                                ? 'border-error-200 bg-error-50/30 hover:bg-error-50/60'
                                : 'border-gray-200 hover:bg-gray-50'
                            )}
                            title={`Click to open Mobile Run #${effectiveMobileRunId} with ${cat.label} expanded`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-gray-800">{cat.label}</p>
                              <p className="text-xs">
                                <span className="text-success-600">{b.passed}</span>
                                <span className="text-gray-400"> / </span>
                                <span className={hasFails ? 'text-error-600 font-medium' : 'text-gray-400'}>
                                  {b.failed} failed
                                </span>
                              </p>
                            </div>
                            {hasFails ? (
                              <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                                {failedTests.map((t, i) => (
                                  <li key={i} className="leading-tight">
                                    <p
                                      className="text-xs text-error-700 font-medium"
                                      title={t.suite ? `${t.suite} › ${t.title}` : t.title}
                                    >
                                      • {t.error_friendly || t.title}
                                    </p>
                                    {t.error_friendly && (
                                      <p className="text-[11px] text-gray-400 ml-2.5 mt-0.5">
                                        {t.title}
                                      </p>
                                    )}
                                    {t.summary && (
                                      <p className="text-[11px] text-gray-500 ml-2.5 mt-0.5 leading-snug">
                                        {t.summary}
                                      </p>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-success-600 italic">All passing</p>
                            )}
                          </Link>
                        );
                      })}
                    </div>

                    {/* Drift surface — show only when unmapped tests exist */}
                    {mobileBreakdown.unmapped && (
                      <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/40">
                        <p className="text-xs font-medium text-amber-800 mb-1">
                          Unmapped tests ({(mobileBreakdown.unmapped.passed || 0) + (mobileBreakdown.unmapped.failed || 0) + (mobileBreakdown.unmapped.skipped || 0)})
                        </p>
                        <p className="text-[11px] text-amber-700">
                          These tests aren't in mobile_journeys.py yet — categorization may be off. Update the config when convenient.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-6">No breakdown available</p>
                )}
              </div>
            )}
          </div>

          {/* Recent Mobile Runs */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-primary-500" />
                Recent Mobile Runs
              </h2>
            </div>
            <div className="divide-y divide-gray-50">
              {mobile_recent_runs && mobile_recent_runs.length > 0 ? (
                mobile_recent_runs.map((run) => (
                  <Link
                    key={`mobile-${run.id}`}
                    to={`/mobile-tests/${run.id}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <StatusDot status={run.total_failures > 0 ? 'failed' : 'passed'} />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Mobile Run #{run.id}
                        </p>
                        <p className="text-xs text-gray-400">
                          {format(new Date(run.timestamp), 'MMM dd, HH:mm')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-medium text-gray-600">
                        {run.total_tests - run.total_failures}/{run.total_tests}
                      </span>
                      {run.total_failures > 0 && (
                        <p className="text-xs text-error-500">{run.total_failures} failed</p>
                      )}
                    </div>
                  </Link>
                ))
              ) : (
                <div className="px-5 py-8 text-center text-sm text-gray-400">
                  No mobile test runs yet
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100">
              <Link to="/test-runs" className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1">
                View all runs <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Health Status */}
      {health_status.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-gray-700">Site Health</h2>
            <Link to="/health" className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
              Details <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {health_status.slice(0, 6).map((check) => (
              <div
                key={check.id}
                className={clsx(
                  'flex items-center justify-between rounded-lg border px-4 py-3',
                  check.is_accessible ? 'border-success-200 bg-success-50/50' : 'border-error-200 bg-error-50/50'
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {check.is_accessible ? (
                    <CheckCircle className="h-4 w-4 text-success-500 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-error-500 shrink-0" />
                  )}
                  <span className="text-sm text-gray-700 truncate">
                    {check.url.replace('https://www.', '')}
                  </span>
                </div>
                <span className="text-xs font-mono text-gray-500 ml-2 shrink-0">
                  {check.response_time.toFixed(0)}ms
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Screenshot modal */}
      {screenshotModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setScreenshotModal(null)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setScreenshotModal(null)}
              className="absolute -top-10 right-0 p-1 text-white/80 hover:text-white"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <img
              src={`${getApiBaseUrl()}/api/screenshots/${screenshotModal}`}
              alt="Test failure screenshot"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Helper Components ---- */

/**
 * Compute a plain-English banner describing site health.
 * Returns { tone: 'green'|'yellow'|'red'|'grey', headline, detail }.
 *
 * Tone rules:
 *   - red:    latest run has ≥5 failures, OR ≥1 monitored URL is unreachable
 *   - yellow: latest run has 1–4 failures, all URLs reachable
 *   - green:  latest run all passing AND all URLs reachable
 *   - grey:   no data
 */
function buildStatusBanner({ latestRun, healthChecks, breakdown }) {
  if (!latestRun) {
    return { tone: 'grey', headline: 'No test runs yet', detail: 'Trigger a run to start monitoring.' };
  }

  // Collapse health checks to "latest per URL"
  const latestPerUrl = {};
  for (const c of healthChecks || []) {
    if (!latestPerUrl[c.url] || new Date(c.timestamp) > new Date(latestPerUrl[c.url].timestamp)) {
      latestPerUrl[c.url] = c;
    }
  }
  const downUrls = Object.values(latestPerUrl).filter((c) => !c.is_accessible);

  const failed = latestRun.failed_tests || 0;
  const cloudflareBlocked = latestRun.cloudflare_blocked_tests || 0;
  const cloudflarePhrase = cloudflareBlocked > 0
    ? `${cloudflareBlocked} skipped by Cloudflare`
    : '';
  let when;
  try { when = formatDistanceToNow(new Date(latestRun.timestamp), { addSuffix: true }); }
  catch { when = ''; }

  // Identify the most-affected category from the breakdown (if loaded)
  let worstCategoryPhrase = '';
  if (breakdown) {
    const ordered = [
      { key: 'used_gear', label: 'Used Gear' },
      { key: 'new_gear', label: 'New Gear' },
      { key: 'connect', label: 'Connect' },
    ]
      .map((c) => ({ ...c, fails: breakdown[c.key]?.failed || 0 }))
      .filter((c) => c.fails > 0)
      .sort((a, b) => b.fails - a.fails);
    if (ordered.length === 1) {
      worstCategoryPhrase = ` in ${ordered[0].label}`;
    } else if (ordered.length > 1) {
      worstCategoryPhrase = ` — most affected: ${ordered[0].label} (${ordered[0].fails})`;
    }
  }

  // Critical
  if (failed >= 5 || downUrls.length > 0) {
    const parts = [];
    if (failed >= 5) parts.push(`${failed} tests broken in Run #${latestRun.id}${worstCategoryPhrase}`);
    if (downUrls.length > 0) {
      const host = (() => { try { return new URL(downUrls[0].url).hostname; } catch { return downUrls[0].url; } })();
      parts.push(`${downUrls.length} monitored URL${downUrls.length > 1 ? 's' : ''} unreachable (${host}${downUrls.length > 1 ? '…' : ''})`);
    }
    if (cloudflarePhrase) parts.push(cloudflarePhrase);
    return {
      tone: 'red',
      headline: 'ATTENTION: action needed',
      detail: parts.join(' · ') + (when ? ` · ${when}` : ''),
    };
  }

  // Yellow
  if (failed > 0) {
    const parts = [
      `${failed} test${failed === 1 ? '' : 's'} failing in Run #${latestRun.id}${worstCategoryPhrase}`,
    ];
    if (cloudflarePhrase) parts.push(cloudflarePhrase);
    parts.push(when);
    return {
      tone: 'yellow',
      headline: 'Mostly healthy',
      detail: parts.filter(Boolean).join(' · '),
    };
  }

  // Cloudflare-only signal — no real failures, but the site bot-blocked a
  // chunk of the suite. Surface as yellow so it isn't lost in the all-green.
  if (cloudflareBlocked > 0) {
    return {
      tone: 'yellow',
      headline: 'Cloudflare blocked part of the run',
      detail: `${cloudflarePhrase} in Run #${latestRun.id} — remaining ${latestRun.passed_tests || 0} test${(latestRun.passed_tests || 0) === 1 ? '' : 's'} passing · ${when}`,
    };
  }

  // Green
  return {
    tone: 'green',
    headline: 'All systems healthy',
    detail: `Latest run #${latestRun.id} ${when}${downUrls.length === 0 && Object.keys(latestPerUrl).length > 0 ? ` · all ${Object.keys(latestPerUrl).length} monitored URLs reachable` : ''}.`,
  };
}

function RunBarTooltip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  if (!p) return null;
  const isRunning = p.status === 'running';
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-md p-2.5 text-xs">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span className="font-semibold text-gray-800">Run #{p.runId}</span>
        {isRunning ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" /> RUNNING
          </span>
        ) : (
          <span className="text-[10px] text-gray-400">{p.name} · {p.time}</span>
        )}
      </div>
      <div className="space-y-0.5">
        <div className="flex justify-between gap-4"><span className="text-success-700">passed</span><span className="text-gray-700">{p.passed}</span></div>
        <div className="flex justify-between gap-4"><span className="text-error-700">failed</span><span className="text-gray-700">{p.failed}</span></div>
        <div className="flex justify-between gap-4"><span className="text-gray-500">skipped</span><span className="text-gray-700">{p.skipped}</span></div>
        {p.cloudflareBlocked > 0 && (
          <div className="flex justify-between gap-4 pt-1 mt-1 border-t border-gray-100">
            <span className="text-amber-700">⚠ cloudflare</span>
            <span className="text-amber-800 font-medium">{p.cloudflareBlocked}</span>
          </div>
        )}
        {p.pending > 0 && (
          <div className="flex justify-between gap-4"><span className="text-primary-700">pending</span><span className="text-gray-700">{p.pending}</span></div>
        )}
      </div>
      {isRunning && (
        <p className="text-[10px] text-gray-400 mt-1.5 pt-1.5 border-t border-gray-100">{p.passed + p.failed + p.skipped} of {p.total} done</p>
      )}
    </div>
  );
}

function StatusBanner({ banner }) {
  const theme = {
    green:  { bg: 'bg-success-50', border: 'border-success-200', icon: <CheckCircle className="h-6 w-6 text-success-600" />, head: 'text-success-800', detail: 'text-success-700' },
    yellow: { bg: 'bg-warning-50', border: 'border-warning-200', icon: <AlertTriangle className="h-6 w-6 text-warning-600" />, head: 'text-warning-800', detail: 'text-warning-700' },
    red:    { bg: 'bg-error-50',   border: 'border-error-200',   icon: <AlertCircle className="h-6 w-6 text-error-600" />,   head: 'text-error-800',   detail: 'text-error-700' },
    grey:   { bg: 'bg-gray-50',    border: 'border-gray-200',    icon: <Clock className="h-6 w-6 text-gray-500" />,           head: 'text-gray-800',    detail: 'text-gray-600' },
  }[banner.tone] || {};
  return (
    <div className={clsx('rounded-xl border-2 px-5 py-4 flex items-center gap-4', theme.bg, theme.border)}>
      <div className="shrink-0">{theme.icon}</div>
      <div className="min-w-0">
        <p className={clsx('text-base font-semibold', theme.head)}>{banner.headline}</p>
        {banner.detail && <p className={clsx('text-sm mt-0.5', theme.detail)}>{banner.detail}</p>}
      </div>
    </div>
  );
}

// "Last Run" card showing the latest web run and latest mobile run side by side.
// The card border reflects the worse of the two statuses so it still signals
// health at a glance.
function LastRunCard({ web, mobile }) {
  const rank = { failed: 3, partial: 2, running: 1, passed: 0 };
  const worst = [web, mobile]
    .filter(Boolean)
    .sort((a, b) => (rank[b.status] || 0) - (rank[a.status] || 0))[0];
  const color = !worst
    ? 'gray'
    : worst.status === 'passed'
    ? 'green'
    : worst.status === 'failed'
    ? 'red'
    : worst.status === 'partial'
    ? 'orange'
    : 'gray';
  const bg = {
    green: 'bg-success-50 border-success-200',
    red: 'bg-error-50 border-error-200',
    orange: 'bg-orange-50 border-orange-200',
    gray: 'bg-gray-50 border-gray-200',
  }[color];

  return (
    <div className={clsx('rounded-xl border p-4', bg)}>
      <p className="text-xs font-medium text-gray-500 mb-2">Last Run</p>
      <LastRunRow label="Web" run={web} />
      <LastRunRow label="Mobile" run={mobile} />
    </div>
  );
}

function LastRunRow({ label, run }) {
  if (!run) {
    return (
      <div className="mb-2 last:mb-0">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="text-sm text-gray-400">No runs</p>
      </div>
    );
  }
  const dot = {
    passed: 'bg-success-500',
    failed: 'bg-error-500',
    partial: 'bg-orange-500',
    running: 'bg-warning-500',
  }[run.status] || 'bg-gray-400';
  const txt = {
    passed: 'text-success-700',
    failed: 'text-error-700',
    partial: 'text-orange-700',
    running: 'text-warning-700',
  }[run.status] || 'text-gray-600';
  return (
    <div className="mb-2 last:mb-0">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className={clsx('text-base font-bold capitalize flex items-center gap-1.5', txt)}>
        <span className={clsx('w-2 h-2 rounded-full shrink-0', dot)} />
        {run.status}
      </p>
      <p className="text-[11px] text-gray-400">{run.time}</p>
    </div>
  );
}

function KpiCard({ label, value, sub, subDim, color, capitalize }) {
  const bg = {
    green: 'bg-success-50 border-success-200',
    red: 'bg-error-50 border-error-200',
    yellow: 'bg-warning-50 border-warning-200',
    orange: 'bg-orange-50 border-orange-200',
    blue: 'bg-primary-50 border-primary-200',
    gray: 'bg-gray-50 border-gray-200',
  }[color] || 'bg-gray-50 border-gray-200';

  const text = {
    green: 'text-success-700',
    red: 'text-error-700',
    yellow: 'text-warning-700',
    orange: 'text-orange-700',
    blue: 'text-primary-700',
    gray: 'text-gray-700',
  }[color] || 'text-gray-700';

  return (
    <div className={clsx('rounded-xl border p-4', bg)}>
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <p className={clsx('text-2xl font-bold', text, capitalize && 'capitalize')}>
        {value}
      </p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      {subDim && <p className="text-[11px] text-gray-400 mt-0.5">{subDim}</p>}
    </div>
  );
}

function StatusDot({ status }) {
  const colors = {
    passed: 'bg-success-500',
    failed: 'bg-error-500',
    partial: 'bg-orange-500',
    running: 'bg-warning-500',
  };
  return (
    <span className={clsx('w-2 h-2 rounded-full shrink-0', colors[status] || 'bg-gray-400')} />
  );
}

export default Dashboard;
